/**
 * Read-only listing of RetentionDeletion rows for the active org.
 * Sweeps + DSR erasures both write here. Compact list — most recent
 * first; payload `summary` is rendered as a small JSON disclosure so
 * admins can drill into a specific row when needed.
 */
import { useQuery } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";

import { api, ApiPaths } from "@/lib/api";

type Row = {
  id: string;
  kind: string;
  target_id: string;
  reason: string;
  occurred_at: string;
  summary: Record<string, unknown>;
};

export function RetentionDeletionLogCard() {
  const { t } = useTranslation();
  const kindLabels: Record<string, string> = {
    session: t("ui.retentionDeletionLog.kindSession"),
    user_erasure: t("ui.retentionDeletionLog.kindUserErasure"),
    participant_erasure: t("ui.retentionDeletionLog.kindParticipantErasure"),
  };
  const q = useQuery<{ results: Row[] }>({
    queryKey: ["org-retention-deletions"],
    queryFn: async () => {
      const res = await api.get<{ results: Row[] }>(
        ApiPaths.orgRetentionDeletions,
      );
      return res.data;
    },
    staleTime: 60_000,
  });

  return (
    <section className="card">
      <h2 className="text-base font-semibold">{t("ui.retentionDeletionLog.title")}</h2>
      <p className="mt-1 text-xs text-nb-text-3">
        {t("ui.retentionDeletionLog.hint")}
      </p>
      {q.isLoading && (
        <p className="mt-3 text-sm text-nb-text-2">{t("ui.retentionDeletionLog.loading")}</p>
      )}
      {q.isError && (
        <p className="mt-3 text-sm text-cvp-danger" role="alert">
          {t("ui.retentionDeletionLog.errorLoad")}
        </p>
      )}
      {q.data && q.data.results.length === 0 && (
        <p className="mt-3 text-sm text-nb-text-2">
          {t("ui.retentionDeletionLog.empty")}
        </p>
      )}
      {q.data && q.data.results.length > 0 && (
        <ul
          className="mt-3 divide-y divide-nb-border"
          data-testid="retention-deletion-log"
        >
          {q.data.results.map((row) => (
            <li key={row.id} className="py-2 text-sm">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">
                  {kindLabels[row.kind] || row.kind}
                </span>
                <span className="font-mono text-xs text-nb-text-3">
                  {formatDateTime(row.occurred_at)}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-nb-text-3">
                {t("ui.retentionDeletionLog.targetLabel")} <code>{row.target_id}</code>{" "}
                {t("ui.retentionDeletionLog.reasonLabel")}{" "}
                <code>{row.reason}</code>
              </div>
              {Object.keys(row.summary).length > 0 && (
                <details className="mt-1 text-xs">
                  <summary className="cursor-pointer text-nb-text-2">
                    {t("ui.retentionDeletionLog.summary")}
                  </summary>
                  <pre className="mt-1 overflow-x-auto rounded bg-nb-bg p-2 text-[10px]">
                    {JSON.stringify(row.summary, null, 2)}
                  </pre>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
